import { Component } from "@theme/component";

/**
 * Hero CTA Slider - Infinite forward-only carousel with polygon crop peek effect.
 *
 * Architecture: 3-panel connected strip (prev + active + next).
 *   - prev:    off-screen left at -100%, slanted edge hidden beyond viewport
 *   - active:  current slide (position:relative for container height, z-index 1)
 *   - next:    off-screen right at +100%, fills peek behind active's clip
 *
 * Each card is wider than 100% on desktop (extends left via negative margin)
 * so the slanted right clip-path is always off-screen when the card is at
 * the prev position (-100%). On transition the entire strip shifts left by
 * 100%: the exiting group (old prev + old active) gets z-index 2 so the
 * diagonal wipe sweeps across the viewport, revealing the new content.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} slidesContainer
 * @property {HTMLElement} [contentWrapper]
 * @property {HTMLElement} [contentTag]
 * @property {HTMLElement} [contentHeading]
 * @property {HTMLElement} [contentDescription]
 * @property {HTMLElement} [contentButtons]
 * @property {HTMLElement[]} [progressSteps]
 *
 * @extends {Component<Refs>}
 */
export class HeroCtaSlider extends Component {
  static #editorEvents = ["block:select", "block:deselect", "section:select", "section:deselect"];

  static get observedAttributes() {
    return ["data-slide-count", "data-autoplay-speed"];
  }

  /** @type {number} */
  #currentIndex = 0;

  /** @type {boolean} */
  #isTransitioning = false;

  /** @type {(() => void) | null} */
  #cancelTransition = null;

  /** @type {number | null} */
  #autoplayTimer = null;

  /** @type {number | null} */
  #touchStartX = null;

  /** @type {boolean} */
  #isInitialized = false;

  /** @type {HTMLElement | null} */
  #contentWrapper = null;

  /** @type {MutationObserver | null} */
  #designModeMutationObserver = null;

  /** @type {HTMLElement[]} */
  #slideWrappers = [];

  /** @type {HTMLElement[]} Cloned wrappers for the 2-slide case */
  #clonedElements = [];

  get #effectiveCount() {
    return this.#slideWrappers.length;
  }

  get slideCount() {
    return parseInt(this.dataset.slideCount ?? "1", 10);
  }

  get autoplaySpeed() {
    return parseInt(this.dataset.autoplaySpeed ?? "5000", 10);
  }

  get sectionId() {
    return this.dataset.sectionId ?? "";
  }

  get isDesignMode() {
    return Boolean(window.Shopify?.designMode);
  }

  get currentSlide() {
    return this.#currentIndex;
  }

  get slidesContainer() {
    return this.refs.slidesContainer ?? this.querySelector('[ref="slidesContainer"]');
  }

  get contentWrapper() {
    return this.#contentWrapper;
  }

  /**
   * Build a translateX transform using the CSS --slide-offset variable.
   * On mobile this is wider than 100% to keep the tilted edge off-screen.
   * @param {number} multiplier - Direction and distance (e.g. 1, -1, 2, -2, 0)
   * @returns {string}
   */
  #tx(multiplier) {
    if (multiplier === 0) return "translateX(0)";
    if (multiplier === 1) return "translateX(var(--slide-offset, 100%))";
    if (multiplier === -1) return "translateX(calc(-1 * var(--slide-offset, 100%)))";
    const abs = Math.abs(multiplier);
    const sign = multiplier < 0 ? "-" : "";
    return `translateX(calc(${sign}${abs} * var(--slide-offset, 100%)))`;
  }

  /* =========================================================================
     LIFECYCLE
     ========================================================================= */

  connectedCallback() {
    super.connectedCallback();
    requestAnimationFrame(() => this.#initialize());
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#destroy();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#destroy();
    requestAnimationFrame(() => this.#initialize());
  }

  #initialize() {
    if (this.#isInitialized) return;
    this.#isInitialized = true;

    const slidesContainer = this.slidesContainer;
    if (!slidesContainer) {
      console.warn("HeroCtaSlider: slidesContainer not found");
      return;
    }

    this.#contentWrapper = this.querySelector('[ref="contentWrapper"]');

    if (this.#contentWrapper) {
      const slider = this.querySelector(".hero-cta__slider");
      if (slider) {
        slider.insertBefore(this.#contentWrapper, slidesContainer.nextSibling);
      }
    }

    this.#slideWrappers = [...slidesContainer.children];

    if (this.slideCount === 2 && !this.isDesignMode) {
      for (const wrapper of [...this.#slideWrappers]) {
        const clone = wrapper.cloneNode(true);
        clone.removeAttribute("data-block-id");
        slidesContainer.appendChild(clone);
        this.#clonedElements.push(clone);
      }
      this.#slideWrappers = [...slidesContainer.children];
    }

    if (this.slideCount > 1) {
      this.#setupSlides();
      this.#preloadAllSlideImages();
      this.#bindEvents();
      this.#startAutoplay();
      this.#updateProgressSteps();
    } else {
      this.#applySlideState(0, "active");
      slidesContainer.classList.add("ready");
    }

    if (this.isDesignMode) {
      this.#setupDesignMode();
    }
  }

  #destroy() {
    if (this.#cancelTransition) this.#cancelTransition();
    this.#unbindEvents();
    this.#pauseAutoplay();
    this.#teardownDesignMode();

    for (const clone of this.#clonedElements) {
      clone.remove();
    }
    this.#clonedElements = [];

    this.#isInitialized = false;
    this.#isTransitioning = false;
    this.#contentWrapper = null;
    this.#slideWrappers = [];
  }

  /* =========================================================================
     SLIDE STATE MANAGEMENT
     ========================================================================= */

  /** @param {HTMLElement} wrapper */
  #clearSlideState(wrapper) {
    wrapper.classList.remove("slide--prev", "slide--active", "slide--next", "slide--exiting", "slide--transitioning");
  }

  /**
   * @param {number} index
   * @param {"prev" | "active" | "next"} state
   */
  #applySlideState(index, state) {
    const wrapper = this.#slideWrappers[index];
    if (!wrapper) return;
    this.#clearSlideState(wrapper);
    wrapper.classList.add(`slide--${state}`);
  }

  #clearAllSlideStates() {
    for (const wrapper of this.#slideWrappers) {
      this.#clearSlideState(wrapper);
    }
  }

  /** @param {number} index */
  #nextIndex(index) {
    return (index + 1) % this.#effectiveCount;
  }

  /** @param {number} index */
  #prevIndex(index) {
    return (index - 1 + this.#effectiveCount) % this.#effectiveCount;
  }

  /* =========================================================================
     SLIDE SETUP
     ========================================================================= */

  #setupSlides() {
    const slidesContainer = this.slidesContainer;
    if (!slidesContainer) return;

    this.#clearAllSlideStates();
    this.#currentIndex = 0;
    this.#applySlideState(0, "active");

    const reveal = () => {
      requestAnimationFrame(() => {
        if (this.#effectiveCount > 1) {
          this.#applySlideState(this.#nextIndex(0), "next");
        }
        if (this.#effectiveCount > 2) {
          this.#applySlideState(this.#prevIndex(0), "prev");
        }
        slidesContainer.classList.add("ready");
      });
    };

    const nextWrapper = this.#effectiveCount > 1 ? this.#slideWrappers[this.#nextIndex(0)] : null;
    const nextImg = nextWrapper?.querySelector("img");

    if (nextImg && !nextImg.complete) {
      nextImg.loading = "eager";
      nextImg.decode().then(reveal).catch(reveal);
      setTimeout(reveal, 500);
    } else {
      reveal();
    }
  }

  /* =========================================================================
     INFINITE FORWARD TRANSITION
     ========================================================================= */

  #advance() {
    if (this.#isTransitioning || this.slideCount <= 1) return;
    if (!this.slidesContainer?.classList.contains("ready")) return;
    this.#isTransitioning = true;

    const oldPrevIdx = this.#prevIndex(this.#currentIndex);
    const oldActiveIdx = this.#currentIndex;
    const oldNextIdx = this.#nextIndex(this.#currentIndex);
    const newNextIdx = this.#nextIndex(oldNextIdx);

    const oldPrevEl = this.#effectiveCount > 2 ? this.#slideWrappers[oldPrevIdx] : null;
    const oldActiveEl = this.#slideWrappers[oldActiveIdx];
    const oldNextEl = this.#slideWrappers[oldNextIdx];
    const reuseOldPrev = oldPrevEl && newNextIdx === oldPrevIdx;
    const canShowNewNext = !reuseOldPrev && newNextIdx !== oldActiveIdx;
    const newNextEl = canShowNewNext ? this.#slideWrappers[newNextIdx] : null;

    if (!oldActiveEl || !oldNextEl) return;

    // Lock starting positions inline
    if (oldPrevEl && !reuseOldPrev) {
      oldPrevEl.style.transform = this.#tx(-1);
    }
    oldActiveEl.style.transform = this.#tx(0);
    oldNextEl.style.transform = this.#tx(1);

    // Assign final roles (new active first to maintain container height)
    this.#clearSlideState(oldNextEl);
    oldNextEl.classList.add("slide--active");

    this.#clearSlideState(oldActiveEl);
    oldActiveEl.classList.add("slide--exiting");

    if (reuseOldPrev) {
      this.#clearSlideState(oldPrevEl);
      oldPrevEl.style.transform = this.#tx(2);
      oldPrevEl.classList.add("slide--next");
    } else if (oldPrevEl) {
      this.#clearSlideState(oldPrevEl);
      oldPrevEl.classList.add("slide--exiting");
    }

    if (newNextEl) {
      this.#clearSlideState(newNextEl);
      newNextEl.style.transform = this.#tx(2);
      newNextEl.classList.add("slide--next");
    }

    void oldActiveEl.offsetHeight;

    // Enable transitions and set targets — entire strip shifts left
    if (oldPrevEl && !reuseOldPrev) {
      oldPrevEl.classList.add("slide--transitioning");
      oldPrevEl.style.transform = this.#tx(-2);
    }
    oldActiveEl.classList.add("slide--transitioning");
    oldActiveEl.style.transform = this.#tx(-1);
    oldNextEl.classList.add("slide--transitioning");
    oldNextEl.style.transform = this.#tx(0);

    if (reuseOldPrev) {
      oldPrevEl.classList.add("slide--transitioning");
      oldPrevEl.style.transform = this.#tx(1);
    }
    if (newNextEl) {
      newNextEl.classList.add("slide--transitioning");
      newNextEl.style.transform = this.#tx(1);
    }

    this.#currentIndex = oldNextIdx;
    this.#updateProgressSteps(oldActiveIdx);
    this.#updateContent(oldNextIdx);

    /** @param {TransitionEvent} e */
    const onTransitionEnd = (e) => {
      if (e.target !== oldActiveEl || e.propertyName !== "transform") return;
      cleanup();
      this.#finishTransition(oldPrevIdx, oldActiveIdx);
    };

    const fallbackTimer = setTimeout(() => {
      cleanup();
      this.#finishTransition(oldPrevIdx, oldActiveIdx);
    }, 2200);

    const cleanup = () => {
      oldActiveEl.removeEventListener("transitionend", onTransitionEnd);
      clearTimeout(fallbackTimer);
      this.#cancelTransition = null;
    };

    this.#cancelTransition = () => {
      cleanup();
      this.#isTransitioning = false;
    };

    oldActiveEl.addEventListener("transitionend", onTransitionEnd);
  }

  /**
   * @param {number} oldPrevIdx
   * @param {number} oldActiveIdx
   */
  #finishTransition(oldPrevIdx, oldActiveIdx) {
    if (this.#effectiveCount > 2) {
      const oldPrevEl = this.#slideWrappers[oldPrevIdx];
      if (oldPrevEl && !oldPrevEl.classList.contains("slide--next")) {
        this.#clearSlideState(oldPrevEl);
        oldPrevEl.style.transform = "";
      }
    }

    const oldActiveEl = this.#slideWrappers[oldActiveIdx];
    if (oldActiveEl) {
      this.#clearSlideState(oldActiveEl);
      oldActiveEl.style.transform = "";
      if (this.#effectiveCount > 2) {
        oldActiveEl.classList.add("slide--prev");
      }
    }

    const newActiveEl = this.#slideWrappers[this.#currentIndex];
    if (newActiveEl) {
      newActiveEl.classList.remove("slide--transitioning");
      newActiveEl.style.transform = "";
    }

    const nextIdx = this.#nextIndex(this.#currentIndex);
    const nextEl = this.#slideWrappers[nextIdx];
    if (nextEl) {
      nextEl.classList.remove("slide--transitioning");
      nextEl.style.transform = "";
      if (!nextEl.classList.contains("slide--next")) {
        this.#applySlideState(nextIdx, "next");
      }
    }

    this.#isTransitioning = false;
  }

  /* =========================================================================
     JUMP TO SLIDE (design mode / progress click)
     ========================================================================= */

  /**
   * Instantly jump to a specific slide without animation.
   * @param {number} index
   * @param {boolean} [forceContentUpdate=false]
   */
  goToSlide(index, forceContentUpdate = false) {
    const currentVisual = this.#currentIndex % this.slideCount;
    const targetVisual = index % this.slideCount;
    if (targetVisual === currentVisual && !forceContentUpdate) return;

    if (this.#cancelTransition) this.#cancelTransition();
    this.#isTransitioning = false;

    const prev = this.#currentIndex;
    this.#currentIndex = index;

    for (const wrapper of this.#slideWrappers) {
      this.#clearSlideState(wrapper);
      wrapper.style.transform = "";
    }

    this.#applySlideState(index, "active");

    if (this.#effectiveCount > 1) {
      this.#applySlideState(this.#nextIndex(index), "next");
    }

    if (this.#effectiveCount > 2) {
      this.#applySlideState(this.#prevIndex(index), "prev");
    }

    const changed = prev % this.slideCount !== targetVisual;
    this.#updateProgressSteps(changed ? prev : null);
    this.#updateContent(index, forceContentUpdate);
    this.#resetAutoplay();
  }

  /**
   * @param {{ index: number }} data
   * @param {Event} event
   */
  handleProgressClick(data, event) {
    event.preventDefault();
    const targetIndex = parseInt(data.index, 10);
    if (Number.isNaN(targetIndex)) return;

    const nextVisual = this.#nextIndex(this.#currentIndex) % this.slideCount;
    if (targetIndex === nextVisual && !this.#isTransitioning) {
      this.#advance();
      this.#resetAutoplay();
    } else {
      this.goToSlide(targetIndex);
    }
  }

  /* =========================================================================
     CONTENT
     ========================================================================= */

  /**
   * @param {number} index
   * @returns {Object | null}
   */
  #getSlideData(index) {
    const slidesContainer = this.slidesContainer;
    const dataElements = slidesContainer?.querySelectorAll(".hero-cta-card__content-data");
    const el = dataElements?.[index];
    if (!el) return null;

    const buttonsSource = el.querySelector("[data-buttons-source]");

    return {
      tag: el.dataset.tag ?? "",
      heading: el.dataset.heading ?? "",
      headingSize: el.dataset.headingSize ?? "large",
      description: el.dataset.description ?? "",
      buttonsHtml: buttonsSource?.innerHTML.trim() ?? ""
    };
  }

  /** @param {Object} data */
  #applyContent({ tag, heading, headingSize, description, buttonsHtml }) {
    const contentWrapper = this.contentWrapper;
    if (!contentWrapper) return;

    const contentTag = contentWrapper.querySelector('[ref="contentTag"]');
    const contentHeading = contentWrapper.querySelector('[ref="contentHeading"]');
    const contentDescription = contentWrapper.querySelector('[ref="contentDescription"]');
    const contentButtons = contentWrapper.querySelector('[ref="contentButtons"]');

    if (contentTag) contentTag.textContent = tag;

    if (contentHeading) {
      contentHeading.textContent = heading;
      contentHeading.className = `hero-cta__heading hero-cta__heading--${headingSize}`;
    }

    if (contentDescription) contentDescription.innerHTML = description;
    if (contentButtons) contentButtons.innerHTML = buttonsHtml;
  }

  /**
   * @param {number} slideIndex
   * @param {boolean} [forceUpdate=false]
   */
  #updateContent(slideIndex, forceUpdate = false) {
    const data = this.#getSlideData(slideIndex);
    const contentWrapper = this.contentWrapper;
    if (!data || !contentWrapper) return;

    const contentTag = contentWrapper.querySelector('[ref="contentTag"]');
    const contentHeading = contentWrapper.querySelector('[ref="contentHeading"]');
    const contentDescription = contentWrapper.querySelector('[ref="contentDescription"]');

    if (this.isDesignMode && forceUpdate) {
      this.#applyContent(data);
      return;
    }

    const contentInner = contentWrapper.querySelector(".hero-cta__content-inner");

    if (!contentInner) {
      this.#applyContent(data);
      return;
    }

    const textEls = [contentTag, contentHeading, contentDescription].filter(Boolean);

    for (const el of textEls) {
      el.style.transition = "opacity 0.4s ease";
      el.style.opacity = "0";
    }

    setTimeout(() => {
      this.#applyContent(data);
      void contentInner.offsetHeight;

      for (const el of textEls) {
        el.style.transition = "opacity 0.6s ease";
        el.style.opacity = "1";
      }

      setTimeout(() => {
        for (const el of textEls) {
          el.style.transition = "";
          el.style.opacity = "";
        }
      }, 700);
    }, 400);
  }

  /* =========================================================================
     PROGRESS INDICATORS
     ========================================================================= */

  /** @param {number | null} [prev=null] */
  #updateProgressSteps(prev = null) {
    const { progressSteps } = this.refs;
    if (!progressSteps) return;

    const activeVisual = this.#currentIndex % this.slideCount;
    const prevVisual = prev !== null ? prev % this.slideCount : null;

    for (let i = 0; i < progressSteps.length; i++) {
      const step = progressSteps[i];
      step.classList.remove("hero-cta__progress-step--leaving");

      if (i === activeVisual) {
        step.classList.add("hero-cta__progress-step--active");
      } else if (i === prevVisual) {
        step.classList.remove("hero-cta__progress-step--active");
        step.classList.add("hero-cta__progress-step--leaving");
        setTimeout(() => step.classList.remove("hero-cta__progress-step--leaving"), 50);
      } else {
        step.classList.remove("hero-cta__progress-step--active");
      }
    }
  }

  /* =========================================================================
     AUTOPLAY
     ========================================================================= */

  #pauseAutoplay() {
    if (this.#autoplayTimer !== null) {
      clearInterval(this.#autoplayTimer);
      this.#autoplayTimer = null;
    }
  }

  #startAutoplay() {
    this.#pauseAutoplay();
    this.#autoplayTimer = window.setInterval(() => this.#advance(), this.autoplaySpeed);
  }

  #resetAutoplay() {
    this.#startAutoplay();
  }

  /* =========================================================================
     IMAGE PRELOADING
     ========================================================================= */

  /** Warm decode only; `loading` / `fetchpriority` come from Liquid in `_hero-cta-slide`. */
  #preloadAllSlideImages() {
    for (const wrapper of this.#slideWrappers) {
      for (const img of wrapper.querySelectorAll("img")) {
        if (!img.complete) img.decode().catch(() => {});
      }
    }
  }

  /* =========================================================================
     EVENTS
     ========================================================================= */

  #bindEvents() {
    const slidesContainer = this.slidesContainer;
    if (slidesContainer) {
      slidesContainer.addEventListener("touchstart", this.#handleTouchStart, { passive: true });
      slidesContainer.addEventListener("touchend", this.#handleTouchEnd, { passive: true });
    }

    document.addEventListener("visibilitychange", this.#handleVisibilityChange);
  }

  #unbindEvents() {
    const slidesContainer = this.slidesContainer;
    if (slidesContainer) {
      slidesContainer.removeEventListener("touchstart", this.#handleTouchStart);
      slidesContainer.removeEventListener("touchend", this.#handleTouchEnd);
    }

    document.removeEventListener("visibilitychange", this.#handleVisibilityChange);
  }

  /** @param {TouchEvent} event */
  #handleTouchStart = (event) => {
    this.#touchStartX = event.changedTouches[0].screenX;
    this.#pauseAutoplay();
  };

  /** @param {TouchEvent} event */
  #handleTouchEnd = (event) => {
    if (this.#touchStartX === null) return;

    const diff = this.#touchStartX - event.changedTouches[0].screenX;

    if (diff > 50) {
      this.#advance();
    }

    this.#touchStartX = null;
    this.#startAutoplay();
  };

  #handleVisibilityChange = () => {
    if (document.hidden) {
      this.#pauseAutoplay();
    } else {
      this.#startAutoplay();
    }
  };

  /* =========================================================================
     DESIGN MODE
     ========================================================================= */

  #setupDesignMode() {
    for (const evt of HeroCtaSlider.#editorEvents) {
      document.addEventListener(`shopify:${evt}`, this.#handleShopifyEditorEvent);
    }

    const slidesContainer = this.slidesContainer;
    if (!slidesContainer) return;

    let timeout;

    this.#designModeMutationObserver = new MutationObserver((mutations) => {
      const hasRelevantMutation = mutations.some(
        (m) =>
          m.type === "childList" ||
          (m.type === "attributes" && m.target.classList?.contains("hero-cta-card__content-data"))
      );

      if (hasRelevantMutation) {
        clearTimeout(timeout);
        timeout = window.setTimeout(() => this.#updateContent(this.#currentIndex, true), 50);
      }
    });

    this.#designModeMutationObserver.observe(slidesContainer, {
      attributes: true,
      subtree: true,
      childList: true,
      attributeFilter: ["data-tag", "data-heading", "data-heading-size", "data-description"]
    });
  }

  #teardownDesignMode() {
    for (const evt of HeroCtaSlider.#editorEvents) {
      document.removeEventListener(`shopify:${evt}`, this.#handleShopifyEditorEvent);
    }

    this.#designModeMutationObserver?.disconnect();
    this.#designModeMutationObserver = null;
  }

  /** @param {CustomEvent} event */
  #handleShopifyEditorEvent = (event) => {
    if (event.detail.sectionId !== this.sectionId) return;

    const eventType = event.type.replace("shopify:", "");

    if (eventType === "block:select") {
      const slidesContainer = this.slidesContainer;
      const blocks = slidesContainer?.querySelectorAll("[data-block-id]");

      if (blocks) {
        const idx = [...blocks].findIndex((b) => b.dataset.blockId === event.detail.blockId);

        if (idx >= 0) {
          this.#pauseAutoplay();
          this.goToSlide(idx, true);
        }
      }
    } else if (eventType.includes("deselect")) {
      this.#startAutoplay();
    } else {
      this.#pauseAutoplay();
    }
  };
}

if (!customElements.get("hero-cta-slider")) {
  customElements.define("hero-cta-slider", HeroCtaSlider);
}
